<script lang='ts' setup>
const { user } = useUserSession();
const route = useRoute();

const props = defineProps({
	collapsed: {
		type: Boolean,
		default: false
	}
});

const notManager = computed(() => {
	return !['admin', 'owner', 'manager'].includes(user.value?.role || '');
});

const isInSection = (paths: string[]) => {
	return paths.some(path => route.path.startsWith(path));
};

const navigationItems = computed(() => {
	const baseItems = [
		[
			{ label: 'Home', icon: 'i-lucide-home', to: '/' },
			{ label: 'Reports', icon: 'i-lucide-file', to: '/report' },
			{ label: 'Docs', icon: 'i-lucide-book', to: '/docs' }
		]
	];

	if (!user.value) {
		return baseItems;
	}

	const userPaths = ['/profile', '/profile/security', '/profile/notifications', '/profile/website'];
	const websitePaths = ['/profile/website/create', '/profile/website/settings', '/profile/website/subscription'];
	const adminPaths = ['/profile/admin/errors', '/profile/admin/users', '/profile/admin/statistics'];

	const userItems = [
		{
			label: 'User',
			icon: 'i-lucide-user',
			defaultOpen: !props.collapsed && isInSection(userPaths),
			type: 'trigger',
			to: props.collapsed ? '/profile' : undefined,
			children: [
				{
					label: 'Profile',
					icon: 'i-lucide-user-check',
					to: '/profile'
				},
				{
					label: 'Security',
					icon: 'i-lucide-shield',
					to: '/profile/security'
				},
				{
					label: 'Notifications',
					icon: 'i-lucide-bell',
					to: '/profile/notifications'
				},
				{
					label: 'Websites',
					icon: 'i-lucide-earth',
					to: '/profile/website'
				}
			]
		}
	];

	const websiteItems = [
		{
			label: 'Website',
			icon: 'i-lucide-globe',
			defaultOpen: isInSection(websitePaths),
			type: 'trigger',
			to: props.collapsed ? '/profile/website' : undefined,
			children: [
				{
					label: 'Create',
					icon: 'i-lucide-plus',
					to: '/profile/website/create'
				},
				{
					label: 'Settings',
					icon: 'i-lucide-settings',
					to: '/profile/website/settings',
					disabled: notManager.value
				},
				{
					label: 'Subscription',
					icon: 'i-lucide-credit-card',
					to: '/profile/website/subscription',
					disabled: notManager.value
				}
			]
		}
	];

	const adminItems = user.value.role === 'admin' ? [
		{
			label: 'Admin',
			icon: 'i-lucide-shield-check',
			defaultOpen: isInSection(adminPaths),
			type: 'trigger',
			to: props.collapsed ? '/profile/admin/users' : undefined,
			children: [
				{
					label: 'Errors',
					icon: 'i-lucide-alert-circle',
					to: '/profile/admin/errors'
				},
				{
					label: 'Users',
					icon: 'i-lucide-users',
					to: '/profile/admin/users'
				},
				{
					label: 'Statistics',
					icon: 'i-lucide-bar-chart',
					to: '/profile/admin/statistics'
				}
			]
		}
	] : [];

	return [
		...baseItems,
		userItems,
		websiteItems,
		...(adminItems.length ? [adminItems] : [])
	];
});

const sidebarNotCollapsed = inject('sidebarNotCollapsed', () => undefined);
</script>

<template>
	<div class="p-3 sticky z-40 flex flex-col space-y-2">
		<div class="hidden md:flex items-center justify-between">
			<h2 v-if="!collapsed" class="font-semibold text-highlighted text-sm">Menu</h2>
			<div class="flex-shrink-0" :class="{ 'mx-auto': collapsed }">
				<DesktopSidebarToggle />
			</div>
		</div>
		<UNavigationMenu class="overflow-y-auto" :collapsed="collapsed" orientation="vertical" variant="link"
			:items="navigationItems" @click="sidebarNotCollapsed" />
	</div>
</template>
